import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, DatePicker, Divider, Drawer, Grid, Popover, Typography, theme } from 'antd';
import { CalendarOutlined, DownOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import {
  ORDER_DATE_PRESETS,
  ORDER_DATE_PRESET_GROUPS,
  ORDER_DATE_PRESET_LABEL,
  isOrderDatePresetKey,
} from './presets';
import { ORDER_DATE_URL_FORMAT, parseOrderDateDraft } from '@/pages/Orders/parts/filters';

const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

export interface DateRangePresetValue {
  date_preset?: string;
  date_from?: string;
  date_to?: string;
}

interface PanelProps {
  preset: string;
  from: string;
  to: string;
  onChange: (value: DateRangePresetValue) => void;
  /** Đóng vật chứa (popover / drawer / filterDropdown của cột) sau khi áp dụng. */
  onClose: () => void;
  fullWidth?: boolean;
}

/** Nhãn trên nút: ưu tiên tên preset, vì preset mới là thứ người dùng đã chọn. */
export function dateRangeTriggerLabel(preset: string, from: string, to: string): string {
  if (isOrderDatePresetKey(preset)) return ORDER_DATE_PRESET_LABEL[preset];
  if (from && to) return `${from} - ${to}`;
  if (from) return `Từ ${from}`;
  if (to) return `Đến ${to}`;
  return 'Chọn thời gian';
}

/**
 * Thân panel chọn thời gian — dùng chung cho FilterBar (bọc trong Popover/Drawer) và cho
 * `filterDropdown` của cột "Thời gian", để hai lối lọc ngày không lệch nhau.
 */
export function DateRangePresetPanel({
  preset,
  from,
  to,
  onChange,
  onClose,
  fullWidth,
}: PanelProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [custom, setCustom] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  // Ô lịch luôn phản ánh khoảng đang áp dụng, kể cả khi panel được giữ mounted
  // (filterDropdown của antd không huỷ nội dung khi đóng).
  useEffect(() => {
    const start = parseOrderDateDraft(from);
    const end = parseOrderDateDraft(to);
    setCustom(start || end ? [start, end] : null);
  }, [from, to]);

  const grouped = useMemo(
    () =>
      ORDER_DATE_PRESET_GROUPS.map((group) => ({
        group,
        items: ORDER_DATE_PRESETS.filter((p) => p.group === group),
      })),
    [],
  );

  const applyPreset = (key: string) => {
    // Chọn preset thì xoá luôn ngày tuyệt đối, nếu không chúng còn lại trên URL và
    // người nhận link sẽ thấy hai nguồn sự thật mâu thuẫn.
    onChange({ date_preset: key, date_from: '', date_to: '' });
    onClose();
  };

  const applyCustom = () => {
    const [start, end] = custom ?? [null, null];
    if (!start && !end) return;
    onChange({
      date_preset: '',
      date_from: start?.startOf('day').format(ORDER_DATE_URL_FORMAT) ?? '',
      date_to: end?.endOf('day').format(ORDER_DATE_URL_FORMAT) ?? '',
    });
    onClose();
  };

  const clearAll = () => {
    onChange({ date_preset: '', date_from: '', date_to: '' });
    setCustom(null);
    onClose();
  };

  return (
    <div
      ref={bodyRef}
      style={{ width: fullWidth ? '100%' : 320, maxWidth: 'calc(100vw - 32px)' }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {grouped.map(({ group, items }) => (
          <div key={group}>
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', marginBottom: 2 }}
            >
              {group}
            </Typography.Text>
            {items.map((p) => (
              <Button
                key={p.key}
                block
                size="small"
                type={preset === p.key ? 'primary' : 'text'}
                // antd Button là inline-flex canh giữa; textAlign không ăn, phải dùng
                // justifyContent thì nhãn mới thẳng lề trái thành cột dễ quét mắt.
                style={{ justifyContent: 'flex-start', paddingInline: 8 }}
                onClick={() => applyPreset(p.key)}
              >
                {p.label}
              </Button>
            ))}
          </div>
        ))}
      </div>

      <Divider style={{ margin: '10px 0 8px' }} />

      <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
        Tùy chọn
      </Typography.Text>
      <RangePicker
        style={{ width: '100%' }}
        format={ORDER_DATE_URL_FORMAT}
        value={custom}
        onChange={(v) => setCustom(v as [Dayjs | null, Dayjs | null] | null)}
        // TCR-2583 — mobile: chọn ngày bằng lịch, không bật bàn phím (bàn phím iOS che lịch
        // và đẩy trang nhảy lên). Desktop vẫn gõ tay được.
        inputReadOnly={fullWidth}
        // Render lịch BÊN TRONG panel: nếu để mặc định (body), click vào lịch bị tính là
        // click ra ngoài và Popover/filterDropdown đóng mất.
        getPopupContainer={() => bodyRef.current ?? document.body}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
        <Button size="small" onClick={clearAll}>
          Xoá
        </Button>
        <Button
          size="small"
          type="primary"
          disabled={!custom?.[0] && !custom?.[1]}
          onClick={applyCustom}
        >
          Áp dụng
        </Button>
      </div>
    </div>
  );
}

interface Props {
  preset: string;
  from: string;
  to: string;
  onChange: (value: DateRangePresetValue) => void;
}

/** Nút hiện khoảng đang lọc; bấm mới mở panel preset. */
export function DateRangePresetPicker({ preset, from, to, onChange }: Props) {
  const { token } = theme.useToken();
  const screens = useBreakpoint();
  const isMobile = screens.md === false;
  const [open, setOpen] = useState(false);

  const panel = (
    <DateRangePresetPanel
      preset={preset}
      from={from}
      to={to}
      onChange={onChange}
      onClose={() => setOpen(false)}
      fullWidth={isMobile}
    />
  );

  const trigger = (
    <Button
      data-testid="date-range-trigger"
      aria-label="Chọn khoảng thời gian"
      icon={<CalendarOutlined />}
      onClick={() => setOpen(!open)}
      style={{
        minWidth: isMobile ? '100%' : 200,
        justifyContent: 'space-between',
        color: isOrderDatePresetKey(preset) || from || to ? token.colorText : undefined,
      }}
    >
      <span style={{ flex: 1, textAlign: 'left' }}>
        {dateRangeTriggerLabel(preset, from, to)}
      </span>
      <DownOutlined style={{ fontSize: 10, color: token.colorTextQuaternary }} />
    </Button>
  );

  if (isMobile) {
    return (
      <>
        {trigger}
        <Drawer
          title="Thời gian"
          placement="bottom"
          height="auto"
          open={open}
          onClose={() => setOpen(false)}
          destroyOnClose
          styles={{ body: { paddingTop: 12 } }}
        >
          {panel}
        </Drawer>
      </>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      content={panel}
      styles={{ body: { padding: 12 } }}
    >
      {trigger}
    </Popover>
  );
}
